import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import { Form, message, Modal } from 'antd';
import type { FormInstance } from 'antd';

import { ChevronDownIcon } from '@/assets/icons/icons';
import { CustomInput } from '../../../components';
import type { SelectedProduct } from '../components/ProductMultiSelectField';
import { ALL_PRODUCTS } from './boxProducts';
import {
  BoxDetails,
  SerialInputs,
  type BoxScopeProps,
  type SerialSlot,
} from './LineBoxDetails';
import { LineStepper } from './LineStepper';
import {
  BOX_REFERENCE_MAX_LENGTH,
  boxesOf,
  boxesWithDataBeyond,
  clampQty,
  duplicateLine,
  emptyLine,
  filled,
  indexOfKey,
  lineHasData,
  lineQty,
  lineSummary,
  lineTitle,
  MAX_BOXES,
  newLineKey,
  packageTotals,
  positiveFiniteNumber,
  remainingCapacity,
  totalBoxes,
  type PackageLineValues,
} from './lines';
import styles from './styles.module.css';

const DIM_FIELDS = [
  { name: 'length', label: 'Length (in)' },
  { name: 'width', label: 'Width (in)' },
  { name: 'height', label: 'Height (in)' },
] as const;

/**
 * What the lines need from the owner's per-line product state, which is keyed by line key and not a form field.
 * Inherits `getProductName(id)` and `serialWarning(lineIndex, box, productId)`.
 */
export interface LineProductsSlot extends SerialSlot {
  /** Product select and items-per-box rows for one line (rendered inside the expanded card). */
  render: (lineIndex: number, line: PackageLineValues) => React.ReactNode;
  productsOf: (lineKey: string) => SelectedProduct[];
  onLineAdded?: (key: string, copiedFromKey?: string) => void;
  onLineRemoved?: (key: string) => void;
  /** A line's key was repaired (a non-string key; never for a missing or repeated one): move its state from `from` to `to`. */
  onKeyReplaced?: (from: string, to: string) => void;
  /** Rendered after the lines when the gate is on (return-mode totals notice). */
  notice?: React.ReactNode;
}

/** Always and centred: the sticky action bar covers the bottom of the drawer body, where 'if-needed' would leave a field. */
export const SCROLL_TO_FIELD = {
  scrollMode: 'always',
  block: 'center',
  focus: true,
} as const;

export interface PackageLinesFieldsHandle {
  /** Expands the line and focuses its first invalid field, else its first control, scrolled into view when it is a form field (the first invalid line after submit). */
  reveal: (lineIndex: number) => void;
}

export interface PackageLinesFieldsProps {
  /** Must carry `lines` in its initialValues (one `emptyLine()` at least): the List adds none of its own. */
  form: FormInstance;
  /** Statsig gate: false renders one line's fields flat with no line chrome (the legacy single-box form). */
  multiBoxEnabled: boolean;
  isReturnMode: boolean;
  /** True once the user pressed Get quote; turns the "Needs weight" tag into "Missing weight". */
  submitAttempted?: boolean;
  products?: LineProductsSlot;
}

const LineButton: React.FC<React.ComponentProps<'button'>> = ({
  className,
  ...props
}) => (
  <button
    type='button'
    className={[styles.lineAction, className].filter(Boolean).join(' ')}
    {...props}
  />
);

const realProducts = (
  slot: LineProductsSlot | undefined,
  key: string
): SelectedProduct[] =>
  slot?.productsOf(key).filter((p) => p.id !== ALL_PRODUCTS) ?? [];

const isPositive = (v: unknown): boolean =>
  positiveFiniteNumber(v) !== undefined;

/** Carriers take whole inches, so a dimension must be at least 1. */
const isDimension = (v: unknown): boolean =>
  (positiveFiniteNumber(v) ?? 0) >= 1;

/** Applies at once, or after the user confirms when the change drops something they typed. */
const confirmRemove = (
  destructive: boolean,
  title: string,
  content: string,
  apply: () => void
): void => {
  if (destructive) {
    Modal.confirm({ title, content, okText: 'Remove', onOk: apply });
  } else {
    apply();
  }
};

// Form.Item names inside <Form.List name='lines'> are relative to the list; dependencies are absolute.
const WeightField: React.FC<{
  index: number;
  qty: number;
  lineLabel: string;
}> = ({ index, qty, lineLabel }) => (
  <Form.Item
    label={qty > 1 ? 'Weight per box (lb)' : 'Weight (lb)'}
    name={[index, 'weight']}
    validateFirst
    rules={[
      { required: true, message: `Enter a weight${lineLabel}.` },
      {
        validator: (_, v) =>
          isPositive(v)
            ? Promise.resolve()
            : Promise.reject(
                new Error(`Weight${lineLabel} must be greater than 0.`)
              ),
      },
    ]}
  >
    <CustomInput placeholder='Enter amount' height='36px' />
  </Form.Item>
);

const DimFields: React.FC<{
  form: FormInstance;
  index: number;
  lineLabel: string;
}> = ({ form, index, lineLabel }) => (
  <div className={styles.dimGrid}>
    {DIM_FIELDS.map(({ name, label }) => {
      const others = DIM_FIELDS.filter((f) => f.name !== name).map((f) => [
        'lines',
        index,
        f.name,
      ]);
      return (
        <Form.Item
          key={name}
          label={label}
          name={[index, name]}
          dependencies={others}
          rules={[
            {
              validator: async (_, v) => {
                const all = [v, ...others.map((p) => form.getFieldValue(p))];
                if (!all.some(filled)) return;
                if (!all.every(isPositive)) {
                  throw new Error(
                    `Enter length, width, and height${lineLabel} — or leave all blank.`
                  );
                }
                if (!all.every(isDimension)) {
                  throw new Error(
                    `Dimensions${lineLabel} must be at least 1 inch.`
                  );
                }
              },
            },
          ]}
        >
          <CustomInput placeholder='Optional' height='36px' />
        </Form.Item>
      );
    })}
  </div>
);

interface LineCardProps {
  form: FormInstance;
  index: number;
  line: PackageLineValues;
  /** Boxes the shipment can still take (see `remainingCapacity`). */
  remaining: number;
  /** Boxes across every line. */
  totalQty: number;
  /** More than one line: messages name the line. */
  several: boolean;
  /** False renders the fields alone: no card, box count or line actions (gate off). */
  chrome: boolean;
  products?: LineProductsSlot;
  onDuplicate?: () => void;
  /** Present only when there is more than one line. */
  onRemove?: () => void;
  onDone?: () => void;
}

const LineCard: React.FC<LineCardProps> = ({
  form,
  index,
  line,
  remaining,
  totalQty,
  several,
  chrome,
  products,
  onDuplicate,
  onRemove,
  onDone,
}) => {
  const qty = lineQty(line);
  const lineLabel = several ? ` for line ${index + 1}` : '';
  const scope: BoxScopeProps = {
    lineIndex: index,
    slot: products,
    lineProducts: realProducts(products, line.key),
  };

  // Resolved from the store when called: props lag a render behind, and a confirm can outlive an index shift.
  const locate = (): { lines: PackageLineValues[]; at: number } => {
    const lines: PackageLineValues[] = form.getFieldValue('lines') ?? [];
    return { lines, at: indexOfKey(lines, line.key) };
  };

  const changeQty = (resolve: (live: number) => number): void => {
    const { lines, at } = locate();
    const current = lines[at];
    if (!current) return;
    const live = lineQty(current);
    const next = clampQty(resolve(live), live + remainingCapacity(lines));
    const removed = live - next;
    if (removed === 0) return;
    confirmRemove(
      removed > 0 && boxesWithDataBeyond(current, next) > 0,
      `Remove ${removed} ${removed === 1 ? 'box' : 'boxes'}?`,
      'Boxes removed from the end will lose what you entered.',
      () => {
        const { lines: now, at: i } = locate();
        const target = now[i];
        // Boxes past the new count go, and so does a stored tail past the old one that a raise would reveal.
        const keep = Math.min(next, live);
        const going = (l: PackageLineValues): string =>
          JSON.stringify(boxesOf(l).slice(keep));
        // Applies to the line as confirmed: same count, same boxes going. A parent write meanwhile (the dialog blocks typing) to either leaves the line alone; one to its other fields is kept.
        if (
          !target ||
          lineQty(target) !== live ||
          going(target) !== going(current)
        ) {
          message.info(
            target
              ? 'That line changed while the dialog was open, so the box count was not applied.'
              : 'That line was removed while the dialog was open.'
          );
          return;
        }
        form.setFieldValue(['lines', i, 'qty'], next);
        if (target.boxes && target.boxes.length > keep) {
          form.setFieldValue(
            ['lines', i, 'boxes'],
            target.boxes.slice(0, keep)
          );
        }
      }
    );
  };

  const fields = (
    <>
      <div className={styles.fieldGrid}>
        {chrome && (
          <Form.Item label='Number of boxes'>
            <LineStepper
              lineNumber={index + 1}
              qty={qty}
              max={qty + remaining}
              onStep={(delta) => changeQty((live) => live + delta)}
              onCommit={(n) => changeQty(() => n)}
            />
          </Form.Item>
        )}
        <WeightField index={index} qty={qty} lineLabel={lineLabel} />
      </div>
      <DimFields form={form} index={index} lineLabel={lineLabel} />
      {products?.render(index, line)}
      {qty === 1 ? (
        <>
          {/* Stays once a value exists, so clearing it does not unmount the focused input. */}
          {(totalQty > 1 || line.boxes?.[0]?.reference !== undefined) && (
            <Form.Item
              label='Box reference (optional)'
              name={[index, 'boxes', 0, 'reference']}
            >
              <CustomInput
                placeholder='Optional'
                height='36px'
                maxLength={BOX_REFERENCE_MAX_LENGTH}
              />
            </Form.Item>
          )}
          <SerialInputs {...scope} box={0} />
        </>
      ) : (
        <BoxDetails {...scope} line={line} />
      )}
    </>
  );
  if (!chrome) return fields;

  return (
    <div
      className={styles.boxCard}
      role='group'
      aria-label={`Line ${index + 1}`}
    >
      <div className={styles.lineCardHeader}>
        <div className={styles.lineCardTitle}>{lineTitle(line)}</div>
        <div className={styles.lineCardActions}>
          <LineButton disabled={remaining === 0} onClick={onDuplicate}>
            Duplicate
          </LineButton>
          {onRemove && <LineButton onClick={onRemove}>Remove</LineButton>}
          {onDone && (
            <LineButton className={styles.lineActionPrimary} onClick={onDone}>
              Done
            </LineButton>
          )}
        </div>
      </div>
      {fields}
    </div>
  );
};

const CollapsedLine: React.FC<{
  index: number;
  line: PackageLineValues;
  products?: LineProductsSlot;
  submitAttempted: boolean;
  onExpand: () => void;
}> = ({ index, line, products, submitAttempted, onExpand }) => (
  <button
    type='button'
    aria-expanded={false}
    className={styles.lineCollapsed}
    data-line-key={line.key}
    onClick={onExpand}
  >
    <span className={styles.lineCollapsedText}>
      <span className={styles.lineCardTitle}>
        <span className={styles.srOnly}>Line {index + 1}, </span>
        {lineTitle(line)}
      </span>
      <span className={styles.lineSummary}>
        {lineSummary(
          line,
          products?.productsOf(line.key),
          products?.getProductName
        )}
      </span>
    </span>
    {!isPositive(line.weight) && (
      <span
        className={`${styles.tag} ${submitAttempted ? styles.tagRed : styles.tagAmber}`}
      >
        {submitAttempted ? 'Missing weight' : 'Needs weight'}
      </span>
    )}
    <ChevronDownIcon size={16} />
  </button>
);

const PackageLinesFields = forwardRef<
  PackageLinesFieldsHandle,
  PackageLinesFieldsProps
>(function PackageLinesFields(
  { form, multiBoxEnabled, isReturnMode, submitAttempted = false, products },
  ref
) {
  // Re-renders on every edit; structure is read from the store below because useWatch lags a task behind.
  const watched: PackageLineValues[] | undefined = Form.useWatch('lines', {
    form,
    preserve: true,
  });
  const readLines = (): PackageLineValues[] =>
    form.getFieldValue('lines') ?? [];
  // undefined = nothing chosen yet (first line); null = all collapsed.
  const [active, setActive] = useState<string | null>();
  // Line whose collapsed button, or first invalid (else first) input, takes focus after the next render.
  const [focusKey, setFocusKey] = useState<string | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);

  const show = (key: string | null, focus: string | null = key): void => {
    setActive(key);
    setFocusKey(focus);
  };

  useImperativeHandle(ref, () => ({
    reveal: (lineIndex) => {
      const key = readLines()[lineIndex]?.key;
      if (key) show(key);
    },
  }));

  // Read by the repair effect below without making the slot a dependency (it is a new object each render).
  const onKeyReplaced = useRef(products?.onKeyReplaced);
  useEffect(() => {
    onKeyReplaced.current = products?.onKeyReplaced;
  });

  // JSON, so a key that only changed type (5 for '5') still re-runs the repair; counts are in it so a stored one is normalized however it arrives.
  const repairKey = JSON.stringify(
    (watched ?? []).map((l) => [l?.key, l?.qty])
  );
  useEffect(() => {
    const lines: PackageLineValues[] = form.getFieldValue('lines') ?? [];
    // Keys are the lines' identity (active card, products slot); a parent write may arrive without them, or repeat one.
    const seen = new Set<string>();
    lines.forEach((l, i) => {
      const old: unknown = l?.key;
      if (typeof old !== 'string' || !old || seen.has(old)) {
        const next = newLineKey();
        form.setFieldValue(['lines', i, 'key'], next);
        // The first holder of a key keeps its state; a missing or repeated key starts without products, so its serials go too (references stay).
        if (old != null && old !== '' && !seen.has(String(old))) {
          seen.add(String(old));
          onKeyReplaced.current?.(String(old), next);
        } else if (l?.boxes?.some((b) => b?.serials)) {
          form.setFieldValue(
            ['lines', i, 'boxes'],
            l.boxes.map((b) => ({ reference: b?.reference }))
          );
        }
      } else {
        seen.add(old);
      }
      // Readers cap a count at MAX_BOXES, so cut the stored one too, else the boxes past the cap would be dropped silently with their references and serials.
      const stored = Math.floor(Number(l?.qty));
      if (stored > MAX_BOXES) {
        form.setFieldValue(['lines', i, 'qty'], MAX_BOXES);
        message.info(
          `Line ${i + 1} listed ${stored} boxes; a shipment has at most ${MAX_BOXES}, so the line was cut to ${MAX_BOXES}.`
        );
      }
    });
    // Errors name the line by position, so fields already in error are re-checked once lines move.
    const errored = form
      .getFieldsError()
      .filter((f) => f.name[0] === 'lines' && f.errors.length > 0)
      .map((f) => f.name);
    if (errored.length > 0) {
      const _ = form.validateFields(errored).catch((): void => undefined);
    }
  }, [form, repairKey]);

  useEffect(() => {
    if (!focusKey) return;
    const target = Array.from(
      rootRef.current?.querySelectorAll<HTMLElement>('[data-line-key]') ?? []
    ).find((el) => el.dataset.lineKey === focusKey);
    const button = target?.matches('button') ? target : undefined;
    const input = button
      ? undefined
      : (target?.querySelector<HTMLElement>('input[aria-invalid="true"]') ??
        target?.querySelector<HTMLElement>('input'));
    if (input?.id) {
      // antd's field id is the name path joined by `_`, and that is also how it looks a path up.
      form.scrollToField(input.id.split('_'), SCROLL_TO_FIELD);
    } else {
      (button ?? input)?.focus();
    }
    setFocusKey(null);
  }, [form, focusKey]);

  return (
    <Form.List name='lines'>
      {(fields, { add, remove }) => {
        const lines = readLines();
        const remaining = remainingCapacity(lines);
        const totalQty = totalBoxes(lines);
        const several = lines.length > 1;

        if (!multiBoxEnabled) {
          return lines[0] ? (
            <div ref={rootRef}>
              <div data-line-key={lines[0].key}>
                <LineCard
                  form={form}
                  index={0}
                  line={lines[0]}
                  remaining={remaining}
                  totalQty={totalQty}
                  several={false}
                  chrome={false}
                  products={products}
                />
              </div>
            </div>
          ) : null;
        }

        const activeKey =
          active === null || lines.some((l) => l?.key === active)
            ? active
            : lines[0]?.key;

        const insertLine = (
          line: PackageLineValues,
          at?: number,
          copiedFromKey?: string
        ): void => {
          // Checked against the store: the disabled states lag a render behind it.
          if (remainingCapacity(readLines()) < lineQty(line)) return;
          add(line, at);
          show(line.key);
          products?.onLineAdded?.(line.key, copiedFromKey);
        };

        const duplicateAt = (key: string): void => {
          const current = readLines();
          const at = indexOfKey(current, key);
          if (at < 0) return;
          insertLine(
            duplicateLine(current[at], remainingCapacity(current)),
            at + 1,
            key
          );
        };

        const removeAt = (key: string): void => {
          const current = readLines();
          const at = indexOfKey(current, key);
          if (at < 0) return;
          confirmRemove(
            lineHasData(current[at], products?.productsOf(key)),
            'Remove this line?',
            'What you entered for these boxes will be lost.',
            () => {
              const now = readLines();
              const i = indexOfKey(now, key);
              if (i < 0) return;
              // Checked here, once: a lone line has no Remove button, so only a write during the dialog gets this far.
              if (now.length < 2) {
                message.info('That line is the only one left, so it stays.');
                return;
              }
              remove(i);
              show(now[i - 1]?.key ?? now[i + 1]?.key ?? null);
              products?.onLineRemoved?.(key);
            }
          );
        };

        return (
          <div ref={rootRef}>
            <div className={styles.boxHint}>
              Identical boxes share one line: change the number of boxes. Add a
              line only when a box is different.
            </div>
            {lines.map((line, i) => {
              // A lone line is always open: `active` can be null from a Done before the lines were replaced.
              const isActive =
                !several || (!!line?.key && line.key === activeKey);
              return (
                <React.Fragment key={fields[i]?.key ?? i}>
                  {!isActive && (
                    <CollapsedLine
                      index={i}
                      line={line}
                      products={products}
                      submitAttempted={submitAttempted}
                      onExpand={() => show(line.key)}
                    />
                  )}
                  {/* Stays mounted when collapsed so antd still validates it. */}
                  <div hidden={!isActive} data-line-key={line?.key}>
                    <LineCard
                      form={form}
                      index={i}
                      line={line}
                      remaining={remaining}
                      totalQty={totalQty}
                      several={several}
                      chrome
                      products={products}
                      onDuplicate={() => duplicateAt(line.key)}
                      onRemove={several ? () => removeAt(line.key) : undefined}
                      onDone={several ? () => show(null, line.key) : undefined}
                    />
                  </div>
                </React.Fragment>
              );
            })}
            <button
              type='button'
              className={styles.addLine}
              disabled={remaining === 0}
              onClick={() => insertLine(emptyLine())}
            >
              Add a different box
            </button>
            {products?.notice}
            {isReturnMode && totalQty > 1 && (
              <div className={styles.boxHint} role='note'>
                Returns with more than one box are booked as standard shipments.
              </div>
            )}
          </div>
        );
      }}
    </Form.List>
  );
});

export default PackageLinesFields;

/** Boxes · Actual weight · Billable weight strip; watches `lines`. */
export const PackageTotals: React.FC<{ form: FormInstance }> = ({ form }) => {
  const lines: PackageLineValues[] | undefined = Form.useWatch('lines', {
    form,
    preserve: true,
  });
  const { boxes, actualLb, billableLb } = packageTotals(lines);
  const items = [
    { label: 'Boxes', value: String(boxes) },
    { label: 'Actual weight', value: `${actualLb} lb` },
    {
      label: 'Billable weight',
      value: billableLb === undefined ? 'Need dims' : `${billableLb} lb`,
    },
  ];
  return (
    <>
      <dl className={styles.totals}>
        {items.map(({ label, value }) => (
          <div key={label} className={styles.totalsItem}>
            <dt className={styles.totalsLabel}>{label}</dt>
            <dd className={styles.totalsValue}>{value}</dd>
          </div>
        ))}
      </dl>
      {billableLb === undefined && (
        <div className={styles.boxHint}>
          Some boxes have no dimensions. Carriers bill on dimensional weight, so
          the quote may change at pickup.
        </div>
      )}
    </>
  );
};
